/**
 * =============================================================================
 * OFFLINE NOTICE COMPONENT
 * =============================================================================
 *
 * Accessible notification component displayed when the user is offline.
 * Shows a user-friendly message with connection status.
 *
 * PATTERN REFERENCE: PHASE_0C_PATTERNS.md §8
 * TASK: 0C-39
 *
 * Accessibility:
 * - Uses role="status" for screen reader announcement (non-interruptive)
 * - Uses aria-live="polite" for non-intrusive updates
 * - High contrast colors meeting WCAG AA requirements
 *
 * Note: Using role="status" + aria-live="polite" instead of role="alert"
 * because role="alert" implies aria-live="assertive" which would conflict.
 *
 * @example
 * ```tsx
 * function QuestionForm() {
 *   const isOnline = useOnlineStatus();
 *
 *   if (!isOnline) {
 *     return <OfflineNotice />;
 *   }
 *
 *   return <Form />;
 * }
 * ```
 *
 * =============================================================================
 */

import React from 'react';
import { useStyleInjection } from '../hooks/useStyleInjection';

export interface OfflineNoticeProps {
  /** Optional custom message to display. Defaults to standard offline message. */
  message?: string;
  /** Optional CSS class name for custom styling */
  className?: string;
}

/**
 * Offline notification banner component.
 *
 * Displays a prominent warning when the user has lost network connectivity,
 * prompting them to check their connection before attempting actions.
 */
export function OfflineNotice({
  message = 'You appear to be offline. Please check your connection and try again.',
  className = '',
}: OfflineNoticeProps): React.ReactElement {
  // Inject styles once, shared across all OfflineNotice instances
  useStyleInjection(offlineStyles, 'fmp-offline-notice-styles');

  return (
    <div
      className={`fmp-offline-notice ${className}`}
      role="status"
      aria-live="polite"
    >
      <svg
        className="fmp-offline-notice__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Wi-Fi off icon */}
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      <p className="fmp-offline-notice__message">{message}</p>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const offlineStyles = `
  :where(.fmp-widget-scope) .fmp-offline-notice {
    display: flex;
    align-items: flex-start;
    gap: var(--fmp-space-md);
    padding: var(--fmp-space-lg);
    margin: var(--fmp-space-lg) 0;
    background-color: var(--fmp-color-warning-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-warning-border);
    border-radius: var(--fmp-radius-control);
    font-family: var(--fmp-font-family);
  }

  :where(.fmp-widget-scope) .fmp-offline-notice__icon {
    flex-shrink: 0;
    width: var(--fmp-size-24);
    height: var(--fmp-size-24);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-offline-notice__message {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-warning-text);
  }
`;
